import { useState, useEffect, useRef } from 'react';
import { X, Upload, Loader2, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Portal } from './Portal';
import { useLanguage } from '../contexts/LanguageContext';
import { log } from '../lib/logger';

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialName?: string;
  initialAvatarPath?: string;
  onSaved: () => void;
}

export function EditProfileDialog({
  open,
  onOpenChange,
  initialName = '',
  initialAvatarPath,
  onSaved,
}: EditProfileDialogProps) {
  const { t, translateError } = useLanguage();
  const [fullName, setFullName] = useState(initialName);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [currentAvatarUrl, setCurrentAvatarUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setFullName(initialName);
    setAvatarFile(null);
    setAvatarPreview('');
    setError('');
    setSuccess(false);
  }, [initialName, open]);

  useEffect(() => {
    const loadAvatarUrl = async () => {
      if (!initialAvatarPath) {
        setCurrentAvatarUrl('');
        return;
      }

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(initialAvatarPath);

      setCurrentAvatarUrl(data.publicUrl);
    };

    if (open) {
      loadAvatarUrl();
    }
  }, [initialAvatarPath, open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onOpenChange]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onOpenChange(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      setError(t('profile.editDialog.imageTypeError'));
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError(t('profile.editDialog.imageSizeError'));
      return;
    }

    setError('');
    setAvatarFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (fullName.trim().length < 2 || fullName.trim().length > 80) {
      setError(t('profile.editDialog.nameError'));
      return;
    }

    try {
      setSaving(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let avatarPath = initialAvatarPath;

      if (avatarFile) {
        setUploading(true);
        const timestamp = Date.now();

        // Sanitize filename: remove special characters, accents, spaces
        const fileExt = avatarFile.name.split('.').pop()?.toLowerCase() || 'jpg';
        const sanitizedName = avatarFile.name
          .split('.')[0]
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '') // Remove accents
          .replace(/[^a-zA-Z0-9]/g, '_') // Replace special chars with underscore
          .substring(0, 50); // Limit length

        const fileName = `${timestamp}_${sanitizedName}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, avatarFile, {
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) throw uploadError;
        avatarPath = filePath;
        setUploading(false);
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          full_name: fullName.trim(),
          avatar_url: avatarPath,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setSuccess(true);
      setTimeout(() => {
        onSaved();
        onOpenChange(false);
      }, 500);
    } catch (err) {
      log.error('Error saving profile:', err);
      setError(translateError(err));
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  if (!open) return null;

  const displayAvatar = avatarPreview || currentAvatarUrl;

  return (
    <Portal>
      <div
        data-testid="dialog-backdrop"
        className="fixed inset-0 z-50 bg-gray-900/40 backdrop-blur-sm backdrop-blur-sm"
        onClick={handleBackdropClick}
      />
      <div
        data-testid="dialog-content"
        className="fixed z-50 inset-0 grid place-items-center p-4"
        onClick={handleBackdropClick}
      >
        <div
          ref={dialogRef}
          className="bg-white rounded-3xl shadow-2xl ring-1 ring-gray-100 w-full max-w-md max-h-[90vh] overflow-y-auto"
        >
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 id="edit-profile-title" className="text-xl font-semibold text-gray-900">
            {t('profile.editDialog.title')}
          </h2>
          <button
            onClick={() => onOpenChange(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close dialog"
            type="button"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-6 space-y-6">
          <div>
            <label htmlFor="full-name" className="block text-sm font-medium text-gray-700 mb-2">
              {t('profile.editDialog.fullName')}
            </label>
            <input
              id="full-name"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder={t('profile.editDialog.fullNamePlaceholder')}
              minLength={2}
              maxLength={80}
              required
              aria-required="true"
              aria-invalid={error.includes('Name') ? 'true' : 'false'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('profile.editDialog.profilePicture')}
            </label>
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center ring-2 ring-gray-200">
                {displayAvatar ? (
                  <img
                    src={displayAvatar}
                    alt="Profile preview"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <User className="w-10 h-10 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">{t('profile.editDialog.uploadImage')}</span>
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  {t('profile.editDialog.imageFormat')}
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleFileChange}
                  className="hidden"
                  aria-label="Upload profile picture"
                />
              </div>
            </div>
          </div>

          {error && (
            <div
              className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
              role="alert"
              aria-live="polite"
            >
              {error}
            </div>
          )}

          {success && (
            <div
              className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700"
              role="alert"
              aria-live="polite"
            >
              {t('profile.editDialog.success')}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={saving || uploading}
            >
              {t('profile.editDialog.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 hover:scale-[1.02]"
            >
              {(saving || uploading) && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{uploading ? t('profile.editDialog.uploading') : saving ? t('profile.editDialog.saving') : t('profile.editDialog.save')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
    </Portal>
  );
}
